// Dependencies
// -----------------------------------------------
import React, { useState } from 'react';
import styled from 'styled-components';

// Helpers
// -----------------------------------------------
import {
  buildAddress,
  buildDateDisplay,
  renderName
} from '../listings/resources/shared-functions';

// Theme tokens (guest "v2-modern" design system, brand-aware with fallbacks)
// -----------------------------------------------
const C = {
  primary: 'var(--primary-cta-color, #20c778)',
  text: 'var(--base-color-text, #525860)',
  heading: 'var(--base-color-black, #040d18)',
  border: 'var(--base-color-border, #dbe1f4)',
  offWhite: 'var(--base-color-off-white, #f8f9f9)'
};

// Styles
// -----------------------------------------------
const Card = styled.div`
  display: flex;
  background: #fff;
  border: 1px solid ${C.border};
  border-radius: 20px;
  box-shadow: 0 24px 48px -24px rgba(4, 13, 24, 0.16);
  overflow: hidden;
  margin-bottom: 24px;

  @media (max-width: 600px) {
    flex-direction: column;
    border-radius: 16px;
  }
`;

const ImageWrap = styled.div`
  flex-shrink: 0;
  width: 220px;
  background: ${C.offWhite};

  img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  @media (max-width: 600px) {
    width: 100%;
    height: 180px;
  }
`;

const NoImage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  min-height: 160px;
  color: ${C.border};

  svg {
    width: 40px;
    height: 40px;
    fill: currentColor;
  }
`;

const Details = styled.div`
  flex: 1;
  padding: 24px 28px;
  min-width: 0;

  @media (max-width: 600px) {
    padding: 20px;
  }
`;

const Eyebrow = styled.div`
  color: ${C.primary};
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  margin-bottom: 6px;
`;

const Name = styled.h2`
  color: ${C.heading};
  font-size: 20px;
  font-weight: 700;
  line-height: 1.2;
  margin: 0 0 4px;
`;

const Address = styled.div`
  color: ${C.text};
  font-size: 14px;
  line-height: 1.45;
`;

const Rule = styled.hr`
  border: none;
  border-top: 1px solid ${C.border};
  margin: 18px 0;
`;

const MetaRow = styled.div`
  display: flex;
  gap: 32px;
  flex-wrap: wrap;
`;

const MetaLabel = styled.div`
  color: ${C.text};
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  margin-bottom: 4px;
`;

const MetaValue = styled.div`
  color: ${C.heading};
  font-size: 15px;
  font-weight: 700;
`;

const ImageIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z" />
  </svg>
);

// -----------------------------------------------
// COMPONENT->BOOKING HEADER ---------------------
// -----------------------------------------------
const BookingHeader = props => {
  const { booking } = props;
  const [imageFailed, setImageFailed] = useState(false);

  if (!booking || !booking.property || !booking.booking) return null;

  const imageUrl =
    booking.featured_image?.image?.small?.url || booking.featured_image?.url;
  const showImage = imageUrl && !imageFailed;

  return (
    <Card>
      <ImageWrap>
        {showImage ? (
          <img
            src={imageUrl}
            alt={renderName(booking)}
            onError={() => setImageFailed(true)}
          />
        ) : (
          <NoImage>
            <ImageIcon />
          </NoImage>
        )}
      </ImageWrap>
      <Details>
        <Eyebrow>You stayed at</Eyebrow>
        <Name>{renderName(booking)}</Name>
        {booking.location && (
          <Address>{buildAddress(booking.location, true)}</Address>
        )}
        <Rule />
        <MetaRow>
          {booking.nights && (
            <div>
              <MetaLabel>{`${booking.nights} Night Stay`}</MetaLabel>
              <MetaValue>
                {buildDateDisplay(
                  booking.booking.check_in,
                  booking.booking.check_out
                )}
              </MetaValue>
            </div>
          )}
          <div>
            <MetaLabel>Booking code</MetaLabel>
            <MetaValue>{booking.booking.booking_code}</MetaValue>
          </div>
        </MetaRow>
      </Details>
    </Card>
  );
};

// Export
// -----------------------------------------------
export default BookingHeader;
